import { RegexRule } from '../types';

/** Minimum length required for new passwords (sign-up, reset, change password). */
export const PASSWORD_MIN_LENGTH = 12;

const PASSWORD_RULE_MESSAGE_ORDER = ['uppercase', 'numbers', 'special', 'lowercase'] as const;

export type PasswordFieldTranslateFn = (key: string, options?: Record<string, string | number>) => string;

export const regexRules: RegexRule[] = [
  {
    pattern: /([A-Z])/,
    name: 'uppercase',
  },
  {
    pattern: /([a-z])/,
    name: 'lowercase',
  },
  {
    pattern: /([0-9])/,
    name: 'numbers',
  },
  {
    pattern: /([^A-Za-z0-9])/,
    name: 'special',
  },
];
/**
 * Utility function to check how many rules (regex) the
 * passed value (password) conforms to
 *
 * @param value - the value to validate
 * @param regexRules - the set of rules to validate against
 *
 * @returns array of rule names that could not be matched
 */
export const checkUnmatchedRules = (value: string, regexRules: RegexRule[]): string[] => {
  const errors = regexRules.reduce<string[]>((prev, curr) => {
    if (!curr.pattern.test(value)) {
      return [...prev, curr.name];
    }
    return prev;
  }, []);
  return errors;
};

/**
 * Validates a password for react-hook-form `rules.validate`.
 * Requires {@link PASSWORD_MIN_LENGTH} characters and every {@link regexRules} class to match.
 *
 * @param value - password field value
 * @param t - translation function
 * @returns `true` if valid, otherwise a translated error string (first failure: length, then uppercase, numbers, special, lowercase)
 */
export const validatePasswordField = (value: string, t: PasswordFieldTranslateFn): true | string => {
  if (value.length < PASSWORD_MIN_LENGTH) {
    return t('ns.common:password:error:min_length', { min: PASSWORD_MIN_LENGTH });
  }

  const unmatched = checkUnmatchedRules(value, regexRules);
  if (unmatched.length === 0) {
    return true;
  }

  for (const ruleName of PASSWORD_RULE_MESSAGE_ORDER) {
    if (unmatched.includes(ruleName)) {
      return t(`ns.common:password:error:${ruleName}`);
    }
  }

  return t('ns.common:password:error:weak');
};
